import React from "react";
import { useNavigate } from "react-router-dom";
import { XCircle, ArrowLeft, RotateCcw, Home, Tent, Mountain, Trees, Heart } from "lucide-react";

function Cancel() {
  const navigate = useNavigate();

  const handleTryAgain = () => {
    window.history.back();
  };

  const handleGoHome = () => {
    navigate("/");
  };

  const handleReturnToLogin = () => {
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        <div className="absolute top-3/4 left-1/2 w-96 h-96 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto">
        <div className="bg-white/80 backdrop-blur-lg border border-white/20 rounded-3xl p-8 shadow-2xl text-center">
          {/* Header with brand elements */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-3 rounded-2xl shadow-lg">
              <Tent className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              CampSite Checker
            </h2>
          </div>

          {/* Icon */}
          <div className="relative mb-8">
            <XCircle className="text-red-500 text-8xl mx-auto" />
            <div className="absolute inset-0 bg-red-400 rounded-full opacity-20 animate-pulse"></div>
          </div>

          {/* Title */}
          <h1 className="text-4xl font-bold text-red-500 mb-4">
            Payment Cancelled
          </h1>

          {/* Message */}
          <p className="text-gray-600 text-lg mb-8 leading-relaxed max-w-lg mx-auto">
            No worries! Your payment was cancelled and no charges were made. 
            You can try again whenever you're ready to unlock premium camping features.
          </p>

          {/* Reassurance Box */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-8 backdrop-blur-sm">
            <div className="flex items-center justify-center mb-4">
              <Heart className="text-green-600 w-8 h-8" />
            </div>
            <div className="space-y-2 text-gray-700">
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>No charges were processed</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Your account remains unchanged</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>You can subscribe anytime</span>
              </div>
            </div>
          </div>

          {/* What you're missing out on */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Premium features waiting for you:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Mountain className="w-4 h-4 text-emerald-600" />
                <span>Real-time availability</span>
              </div>
              <div className="flex items-center gap-2">
                <Trees className="w-4 h-4 text-emerald-600" />
                <span>Advanced search filters</span>
              </div>
              <div className="flex items-center gap-2">
                <Mountain className="w-4 h-4 text-emerald-600" />
                <span>3000+ premium campgrounds</span>
              </div>
              <div className="flex items-center gap-2">
                <Trees className="w-4 h-4 text-emerald-600" />
                <span>Priority support</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleTryAgain}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <RotateCcw size={20} />
              Try Again
            </button>
            
            <button
              onClick={handleReturnToLogin}
              className="flex items-center justify-center gap-2 border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <ArrowLeft size={20} />
              Return to Login
            </button>

            <button
              onClick={handleGoHome}
              className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <Home size={20} />
              Go Home
            </button>
          </div>

          {/* Support Info */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm mb-4">
              Need help? Our support team is here for you 24/7
            </p>
            
            {/* Nature elements for visual consistency */}
            <div className="flex items-center justify-center space-x-4 opacity-60">
              <Mountain className="w-6 h-6 text-emerald-600" />
              <Trees className="w-6 h-6 text-teal-600" />
              <Mountain className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cancel;